import { useCallback, useState } from 'react';
import {
  CheckCircle,
  Clock,
  Circle,
  Shield,
  ChevronRight,
  TrendingUp,
  Check,
} from 'lucide-react';
import { HeroSection } from '../HeroSection';
import { projectApprovalGatesService, gateToUiShape } from '../../services/projectApprovalGates';
import { gateWorkflowService } from '../../services/gateWorkflow';
import { apiErrorMessage } from '../../utils/apiError';
import { Button } from '../ui/button';
import { GateWorkspace } from './GateWorkspace';
import { toast } from 'sonner';
import { useEffect } from 'react';

interface ApprovalGatesSectionProps {
  projectId?: number;
}

export function ApprovalGatesSection({ projectId }: ApprovalGatesSectionProps) {
  const [selectedGateId, setSelectedGateId] = useState<number | null>(null);
  const [apiGates, setApiGates] = useState<Array<{
    id: number;
    number: string;
    name: string;
    status: string;
    progress: number;
    dueDate: string | null;
    completedDate: string | null;
    approver: string;
    documents: { total: number; completed: number };
    decisions: { total: number; approved: number };
    riskStatus: string;
  }>>([]);
  const [loading, setLoading] = useState(false);

  const loadGates = useCallback(() => {
    if (!projectId) {
      setApiGates([]);
      return;
    }
    setLoading(true);
    projectApprovalGatesService
      .list(projectId)
      .then((list) => setApiGates(list.map(gateToUiShape)))
      .catch(() => {
        setApiGates([]);
        toast.error('Failed to load approval gates');
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { loadGates(); }, [loadGates]);

  const [settingUp, setSettingUp] = useState(false);
  const setUpStandardGates = async () => {
    if (!projectId) return;
    setSettingUp(true);
    try {
      await gateWorkflowService.setUpStandard(projectId);
      toast.success('Five standard gates added. Open a gate to see its checklist.');
      loadGates();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not add the gates.'));
    } finally {
      setSettingUp(false);
    }
  };

  // 2026-10-01: the no-project "preview" (fake demo gates, a second gate
  // detail page and a "document editor coming soon" modal) was removed —
  // GateWorkspace is the only gate view.
  const gates = apiGates;

  // Real projects: the working gate view (checklist, uploads, approval).
  if (projectId && selectedGateId !== null) {
    return (
      <GateWorkspace projectId={projectId} gateId={selectedGateId}
        onBack={() => { setSelectedGateId(null); loadGates(); }} onChanged={loadGates} />
    );
  }

  if (projectId && !loading && gates.length === 0) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
          <h3 className="text-lg font-semibold text-foreground">No stage gates yet</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            Gates are the checkpoints where the project needs sign-off before moving on: initiation, planning,
            execution readiness, quality and closure. Each comes with a checklist of documents and decisions.
          </p>
          <Button className="mt-4 min-h-[44px]" onClick={setUpStandardGates} disabled={settingUp}>
            {settingUp ? 'Adding…' : 'Set up the standard five gates'}
          </Button>
        </div>
      </div>
    );
  }

  if (!projectId) {
    return <p className="p-6 text-sm text-muted-foreground">Save the project first to set up its stage gates.</p>;
  }

  return <GatesOverviewPage gates={gates} onSelectGate={setSelectedGateId} loading={loading && !!projectId} />;
}

// Gates Overview Page
function GatesOverviewPage({ gates, onSelectGate, loading }: { gates: any[]; onSelectGate: (id: number) => void; loading?: boolean }) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in-progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-50';
      case 'in-progress':
        return 'border-blue-500 bg-blue-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <div className="text-center text-gray-500">Loading approval gates...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Hero Section */}
      <HeroSection
        title="Approval Gates"
        description="Stage-gate review process ensuring project quality and governance at critical decision points"
        variant="level4"
        metrics={[
          { label: 'Total Gates', value: gates.length.toString(), icon: Shield, iconColor: 'bg-blue-50' },
          {
            label: 'Completed',
            value: gates.filter((g) => g.status === 'completed').length.toString(),
            icon: CheckCircle,
            iconColor: 'bg-green-50',
          },
          {
            label: 'In Progress',
            value: gates.filter((g) => g.status === 'in-progress').length.toString(),
            icon: Clock,
            iconColor: 'bg-amber-50',
          },
          {
            label: 'Overall Progress',
            value: `${Math.round(gates.reduce((sum, g) => sum + g.progress, 0) / gates.length)}%`,
            icon: TrendingUp,
            iconColor: 'bg-purple-50',
          },
        ]}
      />

      {/* Gate Timeline Visual */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl text-neutral-950">Gate Progression Timeline</h2>
          <span className="text-sm text-gray-600">
            Click any gate to view details and manage requirements
          </span>
        </div>
        <div className="flex items-center gap-3 mb-4">
          {gates.map((gate, index) => (
            <div key={gate.id} className="flex items-center flex-1">
              <div
                className={`flex-1 h-3 rounded-full cursor-pointer transition-all hover:h-4 ${
                  gate.status === 'completed'
                    ? 'bg-green-500'
                    : gate.status === 'in-progress'
                      ? 'bg-blue-500'
                      : 'bg-gray-200'
                }`}
                onClick={() => onSelectGate(gate.id)}
              />
              {index < gates.length - 1 && (
                <ChevronRight className="w-5 h-5 text-gray-300 mx-2 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          {gates.map((gate) => (
            <div key={gate.id} className="text-sm text-gray-700 flex-1 text-center font-medium">
              {gate.number}
            </div>
          ))}
        </div>
      </div>

      {/* Gate Cards Grid */}
      <div className="grid grid-cols-5 gap-4">
        {gates.map((gate) => (
          <button
            key={gate.id}
            onClick={() => onSelectGate(gate.id)}
            className={`p-5 rounded-lg border-2 ${getStatusColor(gate.status)} hover:shadow-lg transition-all text-left group`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xl font-semibold mb-1 text-neutral-950">{gate.number}</div>
                <div className="text-sm font-medium text-gray-900">{gate.name}</div>
              </div>
              {getStatusIcon(gate.status)}
            </div>

            {/* Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600 font-medium">Progress</span>
                <span className="text-xs font-semibold text-neutral-950">{gate.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    gate.status === 'completed'
                      ? 'bg-green-500'
                      : gate.status === 'in-progress'
                        ? 'bg-blue-500'
                        : 'bg-gray-300'
                  }`}
                  style={{ width: `${gate.progress}%` }}
                ></div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="space-y-2 mb-4 text-xs">
              <div className="flex items-center justify-between text-gray-600">
                <span>Documents</span>
                <span className="font-semibold text-neutral-950">
                  {gate.documents.completed}/{gate.documents.total}
                </span>
              </div>
              <div className="flex items-center justify-between text-gray-600">
                <span>Decisions</span>
                <span className="font-semibold text-neutral-950">
                  {gate.decisions.approved}/{gate.decisions.total}
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-3 border-t border-gray-300">
              <div className="text-xs text-gray-600 mb-1">
                <span className="font-medium">Due:</span> {gate.dueDate}
              </div>
              {gate.completedDate && (
                <div className="text-xs text-green-600 font-medium flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Completed {gate.completedDate}
                </div>
              )}
            </div>

            {/* Hover Action */}
            <div className="mt-3 flex items-center justify-center gap-1 text-blue-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              View Details <ChevronRight className="w-4 h-4" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Gate Detail Page
